import { useAppDispatch, useAppSelector } from '@hooks/redux-hooks';
import { LoadScript, GoogleMap } from '@react-google-maps/api';
import { loadSitesMapData } from '@telstra-connect/service-performance/methods/load-sites-map-data';
import * as React from 'react'
import Skeleton from 'telstra-ui/components/skeleton/Skeleton';
import Markers from './markers';
import { useRef, useState } from 'react';
import './styles.scss';
import { Application, Routes } from '@applications/service-performance';
import { useNavigate } from 'react-router-dom';
import {
  Links as ServicePerformaceActivityTrackingLinks,
  Components as ServicePerformaceActivityTrackingComponents,
} from '@telstra-connect/service-performance/activity-tracking'
import { NoMarkerData } from '../maps/map-marker-no-data';
import { MapMarkerError } from '../maps/map-marker-error';
import { requestFailed } from '@telstra-connect/utils/data/request-helpers';


interface IGoogleMapProps {
	height: string
	searchInput?: string
	timeRange?: string
	fullscreenControl?: boolean
	zoomControl?: boolean
	zoomLevel?: number
}
const GoogleMaps = ({height, searchInput, timeRange, fullscreenControl, zoomControl, zoomLevel}:IGoogleMapProps) => {
	const dispatch = useAppDispatch()
	React.useEffect(() => {
		dispatch(loadSitesMapData(searchInput, timeRange))
	},[dispatch, searchInput, timeRange])
	
	const sitesData = useAppSelector((state) => state.servicePerformance.sitesMapData)
  const [focusedIndex, setFocusedIndex] = useState(null); // Track the currently focused marker index
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
	const navigate = useNavigate()

	const trackPopUpLink = () => {
		Application.tracker.trackLink(
			ServicePerformaceActivityTrackingComponents.sitesTabComponent,
			ServicePerformaceActivityTrackingLinks.mapMarker,
		)
	}
	// Function to naviagte to detail page
	const handleClick = React.useCallback((siteId) => {
    trackPopUpLink()
    navigate(Application.routes.getPageSubpath(Routes.ViewServicesDetailsBySite(siteId)), {
      state: siteId,
    })
	},[navigate])
  // Function to handle keyboard navigation
  const handleKeyDown = React.useCallback((event) => {
		event.preventDefault()
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      let nextIndex = focusedIndex;
      
      if (event.key === 'ArrowDown') {
        nextIndex = (focusedIndex + 1) % sitesData.data.sites.length;
      } else if (event.key === 'ArrowUp') {
        nextIndex = (focusedIndex - 1 + sitesData.data.sites.length) % sitesData.data.sites.length;
      }
      setFocusedIndex(nextIndex);
    }
		if (event.key === 'Enter' && focusedIndex !== null && sitesData.data.sites[focusedIndex]) {
			handleClick(sitesData.data.sites[focusedIndex].id);
		}
  }, [focusedIndex, sitesData, handleClick]);
  
  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown); // Listen for key events

    return () => {
      window.removeEventListener('keydown', handleKeyDown); // Clean up the event listener
    };
  }, [focusedIndex, handleKeyDown, sitesData]);

	if (sitesData.data.sites.length === 0 && !sitesData.data.mapCentroid) {
		return <NoMarkerData />
	}

	if (requestFailed(sitesData)) {
		return <MapMarkerError onTryAgain={dispatch(loadSitesMapData(searchInput, timeRange))} />
	}

	return(
		<React.Suspense fallback={<Skeleton count={1} width='100%' height={height} />}>
			<div ref={mapContainerRef}>
		<LoadScript googleMapsApiKey={atob(global.applications['Network Performance Reports'].googleMapApiKey)}
			loadingElement={<Skeleton count={1} width='100%' height={height} />}
		>
        <GoogleMap
					ref={mapRef}
          mapContainerStyle={{height, width: '100%'}}
          center={{lat: +sitesData.data.mapCentroid.lat, lng: +sitesData.data.mapCentroid.lon}}
          zoom={zoomLevel ?? 4}
					options={{
						mapId: '464825f82e6676c4',
						disableDefaultUI: true,
						keyboardShortcuts: false,
						fullscreenControl: fullscreenControl ?? true,
						zoomControl: zoomControl ?? true
					}}
        >
          {sitesData.data.sites.map((marker, index) => {
            return (
              marker.fullAddress && marker.wanAvailabilityStatus && <Markers marker={marker} ids={marker.id} key={marker.id} index={index} focusedIndex={focusedIndex}
							 handleClick={handleClick} setFocusedIndex={setFocusedIndex}/>
            )
          }
          )}
        </GoogleMap>
      </LoadScript>
			</div>
			</React.Suspense>
	)
}

export default GoogleMaps;